/**
 * Northstar dropdowns that list a tiered property - statuses, priorities - as a tree, the way the typable pickers
 * draw it: parents before their children in Position order, a child indented under its parent, and the list as wide
 * as its widest option. Used by the ticket page's quick edit and Related Tickets' status filter.
 */

export interface TreeItem {
  Id: number;
  ParentId?: number | null;
  Position?: number;
}

/** Depth-first, siblings in Position order - every parent directly before its children. */
export function sortTreeFlat<T extends TreeItem>(items: T[]): T[] {
  const childrenMap = new Map<number | null, T[]>();
  for (const item of items) {
    const key = item.ParentId ?? null;
    if (!childrenMap.has(key)) childrenMap.set(key, []);
    childrenMap.get(key).push(item);
  }
  for (const [, siblings] of childrenMap) {
    siblings.sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0));
  }
  const result: T[] = [];
  const seen = new Set<number>();
  const walk = (parentId: number | null) => {
    for (const child of childrenMap.get(parentId) ?? []) {
      if (seen.has(child.Id)) continue;
      seen.add(child.Id);
      result.push(child);
      walk(child.Id);
    }
  };
  walk(null);
  return result;
}

/** How many levels down an item sits under its parents (0 for a top one). */
export function depthOf<T extends TreeItem>(item: T, all: T[]): number {
  let depth = 0;
  const seen = new Set<number>([item.Id]);
  for (let parent = all.find(other => other.Id === item.ParentId); parent && !seen.has(parent.Id); parent = all.find(other => other.Id === parent.ParentId)) {
    seen.add(parent.Id);
    depth++;
  }
  return depth;
}

/**
 * A row's left padding: its text starts where the box's own text does (Northstar's 11px left it 1px short), and a
 * child sits 12px further in for each level. A string - a bare number is not turned into pixels here.
 */
export const treeRowStyles = (depth: number) => ({ paddingLeft: `${12 + depth * 12}px` });

/** The open list as wide as its widest option, and never narrower than its box - a long, indented name stays on one line. */
export const LIST_FITS_WIDEST = { width: 'max-content', minWidth: '100%' };
